import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, setPublicKey, uploadKey } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { GmailExportKey } from '../../../../types';
import GeneralDialog from '../../../shared/general-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import AvatarChip from '../../../shared/avatar-chip';

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface UpdateKeyDialogProps {
  open: boolean;
  handleClose: () => void;
  changeLogRoles: string[];
  gmailExportKey: GmailExportKey | undefined;
  toolsRoles: string[];
}

export default function UpdateKeyDialog({
  open,
  handleClose,
  changeLogRoles,
  gmailExportKey,
  toolsRoles,
}: UpdateKeyDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [userKey, setUserKey] = useState('');
  const [keyError, setKeyError] = useState(false);
  const [uploadedKey, setUploadedKey] = useState(false);

  function isPGPKeyBlock(enteredKey: string) {
    const startString = '-----BEGIN PGP PUBLIC KEY BLOCK-----';
    const endString = '-----END PGP PUBLIC KEY BLOCK-----';
    return enteredKey.startsWith(startString) && enteredKey.endsWith(endString);
  }

  const { mutate, isPending } = useMutation({
    mutationFn: () => uploadKey(userKey.trim(), changeLogMessage),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Gmail export key updated successfully!',
      });

      queryClient.setQueryData([UserKeys.GmailExportKey], data);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue updating the gmail export key. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.import_export.gmail_export.update_gmail_export_key,
        {
          module: ModuleList.modules.importExport.name,
          section: ModuleList.modules.importExport.sections.gmailExport,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: setKeyMutate, isPending: setKeyLoading } = useMutation({
    mutationFn: () => setPublicKey(),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Gmail export key set successfully!',
      });

      queryClient.setQueryData([UserKeys.GmailExportKey], data);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue setting the gmail export key. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.import_export.gmail_export.update_gmail_export_key,
        {
          module: ModuleList.modules.importExport.name,
          section: ModuleList.modules.importExport.sections.gmailExport,
        }
      );
    },
  });

  function handleSubmit() {
    if (!isPGPKeyBlock(userKey) && !uploadedKey) {
      setKeyError(true);
    } else {
      setKeyError(false);
      if (uploadedKey) {
        setKeyMutate();
      } else if (
        isAbleToSeeUIElement(changeLogRoles, ToolsRoles.SetGmailExportKey.value)
      ) {
        setOpenChangeLogDialog(true);
      } else {
        mutate();
      }
    }
  }

  return (
    <GeneralDialog
      open={open}
      handleClose={handleClose}
      title="Update Public Encryption Key"
      dialogContent={
        <Stack gap={3} sx={{ px: 4 }}>
          {!gmailExportKey ? (
            <>
              <Typography
                sx={{
                  mt: 1,
                }}
              >
                Please enter your public OpenPGP key for Google to encrypt your
                audits. If you have already uploaded a key through gPanel or
                another application, please check the box below and click save.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={() => setUploadedKey(!uploadedKey)}
                    checked={uploadedKey}
                  />
                }
                label="I have already uploaded a key with gPanel or another application."
              />
            </>
          ) : (
            <Typography
              sx={{
                mt: 1,
              }}
            >
              Please enter your public OpenPGP key for Google to encrypt your
              audits. Uploading a new key will overwrite the key set by{' '}
              <AvatarChip email={gmailExportKey.user} type="User" /> on{' '}
              {new Date(gmailExportKey.date).toLocaleDateString()} at{' '}
              {new Date(gmailExportKey.date).toLocaleTimeString()}.
            </Typography>
          )}
          <Typography>
            If you need more help, please read our{' '}
            <Link
              id="anchor"
              target="_blank"
              rel="noopener noreferrer"
              href={'https://promevo.com/knowledge/create-an-encryption-key'}
            >
              Help Documentation
            </Link>
          </Typography>
          <TextField
            id="key"
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
            placeholder={
              '-----BEGIN PGP PUBLIC KEY BLOCK-----\n Version: Keybase OpenPGP v1.0.0\n Comment: https://keybase.io/crypto\n \n {Key Body}\n \n -----END PGP PUBLIC KEY BLOCK-----'
            }
            error={keyError}
            helperText={
              keyError
                ? `Key must start with "-----BEGIN PGP PUBLIC KEY BLOCK-----" and end with "-----END PGP PUBLIC KEY BLOCK-----".`
                : ' '
            }
            fullWidth
            autoComplete="off"
            value={userKey}
            multiline
            rows={7}
            onChange={(event) => setUserKey(event.target.value)}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Update',
        action: handleSubmit,
        loading: isPending || setKeyLoading,
        disabled:
          isPending ||
          setKeyLoading ||
          !isAbleToSeeUIElement(toolsRoles, ToolsRoles.SetGmailExportKey.value),
      }}
    />
  );
}
